import { MediaLibraryHttpService } from './../../http/media-library-http.service';
import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, skip, switchMap } from 'rxjs/operators';
import { EventsService } from 'src/app/services/events.service';
import { ConfirmModalComponent } from 'src/app/shared/modals/confirm-modal/confirm-modal.component';
import { NewFolderModalComponent } from 'src/app/shared/modals/new-folder-modal/new-folder-modal.component';
import { Subject } from 'rxjs';
import { Utilities } from 'src/app/services/utilities';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { FormControl } from '@angular/forms';
import { EditAssetModalComponent } from 'src/app/shared/modals/edit-asset-modal/edit-asset-modal.component';
import { AlertComponent } from 'src/app/shared/alert/alert.component';

@UntilDestroy()
@Component({
    selector: 'app-media-library',
    templateUrl: './media-library.component.html',
    styles: [],
})
export class MediaLibraryComponent implements OnInit, OnDestroy {
    constructor(
        private modalService: NgbModal,
        private activatedRoute: ActivatedRoute,
        private eventsServices: EventsService,
        private mediaLibraryHttpService: MediaLibraryHttpService,
        private eventsService: EventsService
    ) {}

    params = {
        filter: null,
        page: 0,
        sortBy: 'createdDate',
        isSortAsc: false,
    };

    filterProp = {
        folderId: null,
        assetName: null,
    };

    @Input()
    folders: any[] = [];

    @Input()
    isModal: boolean;

    data: any;

    selected = [];

    uploadPending: boolean;

    searchBoxControl = new FormControl();

    tableDataSub$: Subject<void> = new Subject();

    @ViewChild('table') table: DatatableComponent;

    @ViewChild(AlertComponent) alert: AlertComponent;

    ngOnInit(): void {
        this.routeData();
        this.eventsSub();
        this.tableDataSub();
        this.tableFiltering();
        this.folderSelectFirst();
    }

    routeData() {
        if (!this.isModal) {
            this.activatedRoute.data.subscribe((data) => {
                this.folders = data.data;
            });
        }
    }

    eventsSub() {
        this.eventsServices.trigger.pipe(untilDestroyed(this)).subscribe((data) => {
            if (data.name === 'refreshFolders') {
                this.refreshFolders();
            }

            if (data.name === 'refreshAssets') {
                this.refreshFolderAssets();
            }

            if (data.name === 'getSelectedAsset') {
                this.eventsService.trigger.next({ name: 'selectedAsset', value: this.selected });
            }
        });
    }

    refreshFolders() {
        this.mediaLibraryHttpService.getAllFolders().subscribe((data) => {
            data.forEach((element: any) => {
                let folder = this.folders.find((c) => c.folderId === element.folderId);
                if (!folder) {
                    this.folders.push(element);
                }
                this.alert.addAlert({ type: 'success', message: 'Folder Created successfully' });
            });
        });
    }

    refreshFolderAssets() {
        this.tableDataSub$.next();
    }

    upload(files: any, ev: any) {
        if (files.length === 0) {
            return;
        }

        let file = <File>files[0];

        const formData = new FormData();

        formData.append('file', file, file.name);

        this.uploadPending = true;

        this.mediaLibraryHttpService.upload(formData, this.selectedFolderId).subscribe((data) => {
            this.uploadPending = false;
            ev.target.value = null;
            this.params.page = 0;
            this.refreshFolderAssets();
        });
    }

    confirmDeleteBulk() {
        let confirm = this.modalService.open(ConfirmModalComponent);

        confirm.componentInstance.headerLabel = 'Bulk Action';

        confirm.componentInstance.messageLabel = 'Are you sure you want to delete these items?';

        confirm.result.then(
            (result) => {
                if (result === 'ok') {
                    this.deleteBulk();
                }
            },
            () => {}
        );
    }

    deleteBulk() {
        let ids = this.selected.map((a) => a.assetId);
        this.mediaLibraryHttpService.deleteAssetBulk(ids).subscribe(() => {
            this.refreshFolderAssets();
            this.alert.addAlert({ type: 'success', message: 'Asset Deleted successfully' });
        });
    }

    setActiveFolder(item: any) {
        this.folders.forEach((element: any) => {
            element.active = false;
        });
        item.active = true;
        this.filterProp.folderId = this.selectedFolderId;
        this.params.page = 0;
        this.formateFilter();
        this.refreshFolderAssets();
    }

    openNewFolder() {
        this.modalService.open(NewFolderModalComponent);
    }

    openEditAsset(item: any) {
        let editAsset = this.modalService.open(EditAssetModalComponent);
        editAsset.componentInstance.patchForm(item.assetName, item.assetId);
    }

    confirmDelete(item: any) {
        let confirm = this.modalService.open(ConfirmModalComponent);

        confirm.componentInstance.headerLabel = 'Delete';

        confirm.componentInstance.messageLabel = 'Are you sure you want to delete this content item?';

        confirm.result.then(
            (result) => {
                if (result === 'ok') {
                    this.delete(item);
                }
            },
            () => {}
        );
    }

    delete(item: any) {
        this.mediaLibraryHttpService.deleteAsset(item.assetId).subscribe(() => {
            this.refreshFolderAssets();
            this.alert.addAlert({ type: 'success', message: 'Asset Deleted successfully' });
        });
    }

    confirmFolderDelete(item: any) {
        let confirm = this.modalService.open(ConfirmModalComponent);

        confirm.componentInstance.headerLabel = 'Delete';

        confirm.componentInstance.messageLabel = 'Are you sure you want to delete this content item?';

        confirm.result.then(
            (result) => {
                if (result === 'ok') {
                    this.deleteFolder(item);
                }
            },
            () => {}
        );
    }

    deleteFolder(item: any) {
        this.mediaLibraryHttpService.deleteFolder(item.folderId).subscribe(() => {
            this.removeFolderFromList(item);
        });
    }

    removeFolderFromList(item: any) {
        this.folders = this.folders.filter((c) => c.folderId !== item.folderId);
        this.alert.addAlert({ type: 'success', message: 'Folder Deleted successfully' });
        this.folderSelectFirst();
    }

    formateFilter() {
        this.params.filter = this.filterProp.assetName
            ? `folderId==${this.filterProp.folderId},assetName=*${this.filterProp.assetName}`
            : `folderId==${this.filterProp.folderId}`;
    }

    folderSelectFirst() {
        if (this.folders.length) {
            this.folders[0].active = true;
            this.filterProp.folderId = this.selectedFolderId;
            this.formateFilter();
        }
        this.refreshFolderAssets();
    }

    selectAll() {
        this.table.onHeaderSelect(true);
    }

    tableSelect({ selected }) {
        this.selected.splice(0, this.selected.length);
        this.selected.push(...selected);
    }

    tablePaging(ev: any) {
        this.params.page = ev.offset;
        this.refreshFolderAssets();
    }

    tableSorting(event: any) {
        this.params.sortBy = event.sorts[0].prop;
        this.params.isSortAsc = event.newValue === 'asc' ? true : false;
        this.params.page = 0;
        this.refreshFolderAssets();
    }

    tableFiltering() {
        this.searchBoxControl.valueChanges.subscribe((text: string) => {
            this.filterProp.assetName = text;
            this.params.page = 0;
            this.formateFilter();
            this.refreshFolderAssets();
        });
    }

    tableDataSub() {
        this.tableDataSub$
            .pipe(
                skip(1),
                switchMap(() => {
                    return this.tableData();
                })
            )
            .subscribe((data) => {
                this.data = data;
            });
    }

    tableData() {
        this.selected = [];
        return this.mediaLibraryHttpService.getFolderAssets(this.params);
    }

    formatBytes(item: any) {
        let dataBytes = item.assetData.find((c: { fit: string }) => c.fit === 'small').size;
        return Utilities.formatBytes(dataBytes);
    }

    get selectedFolderId() {
        return this.folders?.find((c) => c.active)?.folderId;
    }

    ngOnDestroy(): void {
        this.eventsService.trigger.next({ name: null, value: null });
    }
}
